<template>
  <DialogHeader> Remove access? </DialogHeader>
  <DialogContent>
    Are you sure you want to remove {{ user }}'s access to this file?
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      danger
      :loading="loading"
      @click="handleClick"
    >
      Remove
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { graphql } from '@/shared/graphql/router'
import {
  InviteType,
  type ViewersConnection,
  type InviteInput,
} from '@/shared/graphql/router/graphql'
import type { Reference } from '@apollo/client'
import { useMutation } from '@vue/apollo-composable'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { computed } from 'vue'
import { useUserStore } from '@/shared/stores/user'

const REMOVE_VIEWER_MUTATION = graphql(`
  mutation RemoveViewer($input: RemoveViewerInput!) {
    removeViewer(input: $input) {
      success
    }
  }
`)

const { close, triggerEvent } = useDialog()

const props = defineProps<{
  fileId: string
  invite: InviteInput
  name: string
}>()

const { mutate: removeViewer, loading } = useMutation(REMOVE_VIEWER_MUTATION, {
  clientId: 'router',
})

const toastStore = useToastStore()
const userStore = useUserStore()

const user = computed(() =>
  props.invite.id === userStore.user?.id ? 'your' : `${props.name}`
)

const handleClick = async () => {
  try {
    await removeViewer(
      { input: { fileId: props.fileId, invite: props.invite } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.fileId }),
            fields: {
              viewers(existing, { readField }) {
                const data = existing as ViewersConnection
                if (!data?.edges) return existing

                return {
                  ...data,
                  edges: data.edges.filter(({ node }) => {
                    if (props.invite.type === InviteType.Email) {
                      return readField('email', node) !== props.invite.email
                    }

                    const entity = readField<Reference>('entity', node)
                    return entity
                      ? readField('id', entity) !== props.invite.id
                      : true
                  }),
                }
              },
            },
          })
        },
      }
    )

    close()
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later',
      error: error as Error,
    })
  }
}
</script>
